import type React from 'react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { usePropertiesQuery } from '@/hooks/queries/useProperties';
import { useUsersQuery } from '@/hooks/queries/useUsers';
import {
  useAvailableScopeQuery,
  useCreateAssessmentMutation,
} from '@/hooks/queries/useAssessments';
import type { CreateAssessmentRequest } from '@/types/api';

export function SelfAssessmentForm() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);

  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState<number[]>([]);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<Set<number>>(
    new Set()
  );
  const [selectedTouchpoints, setSelectedTouchpoints] = useState<
    Map<number, Set<number>>
  >(new Map());
  const [dueDate, setDueDate] = useState('');
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch properties
  const { data: propertiesResponse, isLoading: propertiesLoading } =
    usePropertiesQuery({ page_size: 100 });
  const properties = propertiesResponse?.data || [];

  // Fetch assignable users (filtered based on current user's role)
  const { data: usersResponse, isLoading: usersLoading } = useUsersQuery({
    page_size: 100,
    for_assignment: 'true',
  });
  const assignableUsers = usersResponse?.data || [];

  // Fetch available scope when property is selected
  const { data: scopeResponse, isLoading: scopeLoading } =
    useAvailableScopeQuery(selectedProperty ? parseInt(selectedProperty) : 0);
  const availableScope = scopeResponse?.data?.available_scope || [];

  // Create assessment mutation
  const createMutation = useCreateAssessmentMutation({
    onSuccess: () => {
      navigate('/assessments');
    },
  });

  // Get selected property object
  const selectedPropertyObject = useMemo(() => {
    return properties.find(p => p.id.toString() === selectedProperty);
  }, [properties, selectedProperty]);

  // Filter assignable users based on search term
  const filteredAssignableUsers = useMemo(() => {
    if (!assigneeSearchTerm) return assignableUsers;
    const searchLower = assigneeSearchTerm.toLowerCase();
    return assignableUsers.filter(
      user =>
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
    );
  }, [assignableUsers, assigneeSearchTerm]);

  // Get selected assignee objects
  const selectedAssigneeObjects = useMemo(() => {
    return assignableUsers.filter(user => selectedAssignees.includes(user.id));
  }, [assignableUsers, selectedAssignees]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(event.target as Node)
      ) {
        setShowAssigneeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddAssignee = (userId: number) => {
    if (!selectedAssignees.includes(userId)) {
      setSelectedAssignees([...selectedAssignees, userId]);
    }
    setAssigneeSearchTerm('');
    setShowAssigneeDropdown(false);
  };

  const handleRemoveAssignee = (userId: number) => {
    setSelectedAssignees(selectedAssignees.filter(id => id !== userId));
  };

  const handleDepartmentToggle = (departmentId: number) => {
    const newSelectedDepartments = new Set(selectedDepartments);
    const newSelectedTouchpoints = new Map(selectedTouchpoints);

    if (newSelectedDepartments.has(departmentId)) {
      // Uncheck department - remove all touchpoints
      newSelectedDepartments.delete(departmentId);
      newSelectedTouchpoints.delete(departmentId);
    } else {
      // Check department - select all touchpoints
      newSelectedDepartments.add(departmentId);
      const department = availableScope.find(
        d => d.department_id === departmentId
      );
      if (department) {
        const touchpointIds = new Set(
          department.touchpoints.map(t => t.touchpoint_id)
        );
        newSelectedTouchpoints.set(departmentId, touchpointIds);
      }
    }

    setSelectedDepartments(newSelectedDepartments);
    setSelectedTouchpoints(newSelectedTouchpoints);
  };

  const handleTouchpointToggle = (
    departmentId: number,
    touchpointId: number
  ) => {
    const newSelectedTouchpoints = new Map(selectedTouchpoints);
    const newSelectedDepartments = new Set(selectedDepartments);

    const departmentTouchpoints =
      newSelectedTouchpoints.get(departmentId) || new Set();

    if (departmentTouchpoints.has(touchpointId)) {
      departmentTouchpoints.delete(touchpointId);
    } else {
      departmentTouchpoints.add(touchpointId);
    }

    if (departmentTouchpoints.size === 0) {
      newSelectedTouchpoints.delete(departmentId);
      newSelectedDepartments.delete(departmentId);
    } else {
      newSelectedTouchpoints.set(departmentId, departmentTouchpoints);
      newSelectedDepartments.add(departmentId);
    }

    setSelectedDepartments(newSelectedDepartments);
    setSelectedTouchpoints(newSelectedTouchpoints);
  };

  const isDepartmentIndeterminate = (departmentId: number) => {
    const department = availableScope.find(
      d => d.department_id === departmentId
    );
    if (!department) return false;

    const selected = selectedTouchpoints.get(departmentId);
    if (!selected) return false;

    return selected.size > 0 && selected.size < department.touchpoints.length;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (selectedAssignees.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one assignee',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedProperty) {
      toast({
        title: 'Validation Error',
        description: 'Please select a property',
        variant: 'destructive',
      });
      return;
    }

    if (!dueDate) {
      toast({
        title: 'Validation Error',
        description: 'Please set a due date',
        variant: 'destructive',
      });
      return;
    }

    if (!subject.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a subject',
        variant: 'destructive',
      });
      return;
    }

    if (selectedTouchpoints.size === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one department and touchpoint',
        variant: 'destructive',
      });
      return;
    }

    // Build scope array
    const scope: CreateAssessmentRequest['scope'] = [];
    selectedTouchpoints.forEach((touchpointIds, departmentId) => {
      scope.push({
        department_id: departmentId,
        touchpoint_ids: Array.from(touchpointIds),
      });
    });

    const requestData: CreateAssessmentRequest = {
      property_id: parseInt(selectedProperty),
      assigned_to: selectedAssignees,
      subject: subject.trim(),
      notes: notes.trim() || undefined,
      due_date: dueDate,
      scope,
    };

    createMutation.mutate(requestData);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-4 sm:py-6 lg:py-8 px-4 sm:px-6 max-w-6xl">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 sm:gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm sm:text-base"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary">
              New Self Assessment
            </h1>
            {selectedPropertyObject && (
              <div className="text-base sm:text-lg text-muted-foreground">
                <div className="font-semibold text-sm sm:text-base">
                  {selectedPropertyObject.name}
                </div>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Form Configuration */}
          <Card>
            <CardContent className="pt-4 sm:pt-6 space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Property Selection */}
                <div className="space-y-2">
                  <Label htmlFor="property" className="text-sm font-medium">
                    Choose Property
                  </Label>
                  <Select
                    value={selectedProperty}
                    onValueChange={value => {
                      setSelectedProperty(value);
                      // Reset scope selection when property changes
                      setSelectedDepartments(new Set());
                      setSelectedTouchpoints(new Map());
                    }}
                    disabled={propertiesLoading}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={
                          propertiesLoading
                            ? 'Loading properties...'
                            : 'Select a property...'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map(property => (
                        <SelectItem
                          key={property.id}
                          value={property.id.toString()}
                        >
                          {property.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Assignee Field with Multiple Selection */}
                <div className="space-y-2">
                  <Label htmlFor="assignee" className="text-sm font-medium">
                    Assignee(s)
                  </Label>
                  <div className="space-y-2">
                    {/* Selected Assignees */}
                    {selectedAssigneeObjects.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedAssigneeObjects.map(user => (
                          <Badge
                            key={user.id}
                            variant="secondary"
                            className="px-2 py-1"
                          >
                            {user.name}
                            <button
                              type="button"
                              onClick={() => handleRemoveAssignee(user.id)}
                              className="ml-1 hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Search Input */}
                    <div className="relative" ref={assigneeDropdownRef}>
                      <Input
                        id="assignee"
                        type="text"
                        placeholder={
                          usersLoading
                            ? 'Loading users...'
                            : 'Search and select users...'
                        }
                        value={assigneeSearchTerm}
                        onChange={e => {
                          setAssigneeSearchTerm(e.target.value);
                          setShowAssigneeDropdown(true);
                        }}
                        onFocus={() => setShowAssigneeDropdown(true)}
                        disabled={usersLoading}
                        className="w-full"
                      />

                      {/* Dropdown */}
                      {showAssigneeDropdown && filteredAssignableUsers.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                          {filteredAssignableUsers.map(user => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => handleAddAssignee(user.id)}
                              disabled={selectedAssignees.includes(user.id)}
                              className="w-full px-3 py-2 text-left hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <div className="font-medium">{user.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {user.email}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Departments from Property Scope */}
          {selectedProperty && (
            <Card>
              <CardHeader className="pb-4 sm:pb-6">
                <CardTitle className="text-lg sm:text-xl">
                  {scopeLoading
                    ? 'Loading departments...'
                    : 'Available Departments & Touchpoints'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6">
                {scopeLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading available scope...
                  </div>
                ) : availableScope.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No departments configured for this property
                  </div>
                ) : (
                  availableScope.map(department => (
                    <div
                      key={department.department_id}
                      className="space-y-3 sm:space-y-4"
                    >
                      <div className="flex items-center space-x-2 sm:space-x-3 bg-muted px-3 sm:px-4 py-2 sm:py-3 rounded-md">
                        <Checkbox
                          id={`dept-${department.department_id}`}
                          checked={selectedDepartments.has(
                            department.department_id
                          )}
                          onCheckedChange={() =>
                            handleDepartmentToggle(department.department_id)
                          }
                          className={
                            isDepartmentIndeterminate(department.department_id)
                              ? 'data-[state=checked]:bg-primary/50'
                              : ''
                          }
                        />
                        <Label
                          htmlFor={`dept-${department.department_id}`}
                          className="font-semibold text-base sm:text-lg text-primary cursor-pointer flex-1"
                        >
                          {department.department_name}
                        </Label>
                        <span className="text-sm text-muted-foreground">
                          ({department.touchpoints.length} touchpoints)
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3 pl-4 sm:pl-6">
                        {department.touchpoints.map(touchpoint => (
                          <div
                            key={touchpoint.touchpoint_id}
                            className="flex items-start space-x-2 sm:space-x-3 p-2 hover:bg-muted/50 rounded-md transition-colors"
                          >
                            <Checkbox
                              id={`tp-${department.department_id}-${touchpoint.touchpoint_id}`}
                              checked={
                                selectedTouchpoints
                                  .get(department.department_id)
                                  ?.has(touchpoint.touchpoint_id) || false
                              }
                              onCheckedChange={() =>
                                handleTouchpointToggle(
                                  department.department_id,
                                  touchpoint.touchpoint_id
                                )
                              }
                              className="mt-0.5"
                            />
                            <Label
                              htmlFor={`tp-${department.department_id}-${touchpoint.touchpoint_id}`}
                              className="text-xs sm:text-sm font-medium leading-relaxed cursor-pointer flex-1"
                            >
                              {touchpoint.touchpoint_name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          )}

          {/* Additional Information */}
          <Card>
            <CardContent className="pt-4 sm:pt-6 space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <Label htmlFor="due-date" className="text-sm font-medium">
                    Due Date
                  </Label>
                  <Input
                    id="due-date"
                    type="date"
                    value={dueDate}
                    onChange={e => setDueDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-sm font-medium">
                    Subject
                  </Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder="Enter assessment subject..."
                    className="w-full"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-sm font-medium">
                  Notes
                </Label>
                <Textarea
                  id="notes"
                  rows={4}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Add any additional notes or comments..."
                  className="w-full resize-none"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row justify-start gap-3 sm:gap-4 pt-4">
            <Button
              type="submit"
              className="w-full sm:w-auto px-6 sm:px-8 py-2"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Assessment'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto px-6 sm:px-8 py-2"
              onClick={() => navigate('/assessments')}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CreateAssessmentPage() {
  return <SelfAssessmentForm />;
}
